import React, { useEffect, useMemo, useState } from 'react';
import { FiActivity, FiAlertTriangle, FiCpu, FiDatabase, FiRefreshCw, FiShield } from 'react-icons/fi';
import * as api from '../api/api';
import LoadingSpinner from '../components/LoadingSpinner';
import './Logs.css';

const fmtTime = (value) => {
  if (!value) return '—';
  try { return new Date(value).toLocaleString(); }
  catch (_) { return '—'; }
};

const toneForLevel = (level) => {
  const value = String(level || '').toLowerCase();
  if (value === 'error' || value === 'critical') return 'bad';
  if (value === 'warning' || value === 'warn') return 'warn';
  return 'good';
};

const Logs = () => {
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [health, setHealth] = useState(null);
  const [intel, setIntel] = useState(null);
  const [bot, setBot] = useState(null);
  const [filter, setFilter] = useState('ALL');
  const [error, setError] = useState(null);

  const refresh = async () => {
    try {
      setError(null);
      const results = await Promise.allSettled([
        api.getLogs(),
        api.getLocalHealth(),
        api.getIntelligenceStatus(),
        api.getBotStatus(),
      ]);
      if (results[0].status === 'fulfilled') setLogs(results[0].value.data || []);
      else setError('Could not load diagnostic logs.');
      if (results[1].status === 'fulfilled') setHealth(results[1].value.data);
      if (results[2].status === 'fulfilled') setIntel(results[2].value.data);
      if (results[3].status === 'fulfilled') setBot(results[3].value.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    const timer = setInterval(refresh, 15000);
    return () => clearInterval(timer);
  }, []);

  const filteredLogs = useMemo(() => {
    if (filter === 'ALL') return logs;
    return logs.filter(row => String(row.level || '').toUpperCase() === filter);
  }, [logs, filter]);

  const errorCount = logs.filter(row => ['ERROR','CRITICAL'].includes(String(row.level || '').toUpperCase())).length;
  const warningCount = logs.filter(row => ['WARNING','WARN'].includes(String(row.level || '').toUpperCase())).length;
  const runtimeReady = Boolean(health?.runtime_alive);
  const providerReady = Boolean(intel?.configured);
  const brokerReady = Boolean(health?.broker_connected);
  const paperLocked = !health?.live_unlocked;

  if (loading) return <div className="workspace-page"><LoadingSpinner /></div>;

  return (
    <div className="workspace-page diagnostics-page">
      <div className="page-header">
        <div className="page-header-copy">
          <div className="eyebrow">System observability</div>
          <h1 className="page-title">Diagnostics</h1>
          <p className="page-description">Runtime, data, broker safety and execution logs in one operational view.</p>
        </div>
        <div className="page-actions">
          <button className="ws-button" onClick={refresh}><FiRefreshCw/> Refresh</button>
        </div>
      </div>

      {error && <div className="ws-banner danger"><FiAlertTriangle/><div>{error}</div></div>}
      {health?.last_error && (
        <div className="ws-banner danger">
          <FiAlertTriangle/>
          <div><strong>Runtime stopped fail-closed.</strong> {health.last_error}</div>
        </div>
      )}

      <div className="diagnostic-status-grid">
        <div className={'diagnostic-status-card ' + (runtimeReady ? 'good' : 'warn')}>
          <FiCpu/>
          <div><span>Runtime</span><strong>{runtimeReady ? 'ONLINE' : 'IDLE'}</strong><small>{health?.failed_ticks || 0} failed ticks</small></div>
        </div>
        <div className={'diagnostic-status-card ' + (providerReady ? 'good' : 'warn')}>
          <FiDatabase/>
          <div><span>Market data</span><strong>{intel?.provider || 'NONE'}</strong><small>{intel?.synthetic_data ? 'synthetic demo' : providerReady ? 'configured' : 'not configured'}</small></div>
        </div>
        <div className={'diagnostic-status-card ' + (brokerReady ? 'good' : 'muted')}>
          <FiActivity/>
          <div><span>Broker</span><strong>{brokerReady ? 'IBKR CONNECTED' : 'OPTIONAL / OFFLINE'}</strong><small>{health?.broker_connection_id ? 'connection #' + health.broker_connection_id : 'no active paper session'}</small></div>
        </div>
        <div className={'diagnostic-status-card ' + (paperLocked ? 'good' : 'bad')}>
          <FiShield/>
          <div><span>Safety</span><strong>{paperLocked ? 'PAPER LOCKED' : 'LIVE UNLOCKED'}</strong><small>{bot?.is_running ? 'engine running' : 'engine stopped'}</small></div>
        </div>
      </div>

      <div className="diagnostic-meta-strip">
        <div><span>Last tick</span><strong>{fmtTime(health?.last_tick_finished_at)}</strong></div>
        <div><span>Successful ticks</span><strong>{health?.successful_ticks || 0}</strong></div>
        <div><span>Errors</span><strong className={errorCount ? 'bad' : ''}>{errorCount}</strong></div>
        <div><span>Warnings</span><strong className={warningCount ? 'warn' : ''}>{warningCount}</strong></div>
        <div><span>Scanner job</span><strong>{intel?.running_job || 'IDLE'}</strong></div>
      </div>

      <section className="ws-panel diagnostics-log-panel">
        <div className="diagnostics-log-head">
          <div>
            <span>EVENT LOG</span>
            <em>{filteredLogs.length} shown · auto refresh 15s</em>
          </div>
          <div className="log-filter-tabs">
            {['ALL','ERROR','WARNING','INFO'].map(item => (
              <button key={item} className={filter === item ? 'active' : ''} onClick={() => setFilter(item)}>
                {item}
              </button>
            ))}
          </div>
        </div>

        {filteredLogs.length ? (
          <div className="diagnostic-table-wrap">
            <table className="diagnostic-table">
              <thead>
                <tr><th>Time</th><th>Level</th><th>Message</th></tr>
              </thead>
              <tbody>
                {filteredLogs.map((log, index) => (
                  <tr key={log.id || index}>
                    <td>{fmtTime(log.timestamp)}</td>
                    <td><span className={'diagnostic-level ' + toneForLevel(log.level)}>{log.level || 'INFO'}</span></td>
                    <td>{log.message || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="diagnostic-empty">
            <FiActivity/>
            <strong>No diagnostic events</strong>
            <span>The runtime has not recorded any matching log events yet.</span>
          </div>
        )}
      </section>
    </div>
  );
};

export default Logs;
